import {
 Box,
 FormControl,
 InputLabel,
 MenuItem,
 Select,
 Typography,
} from '@mui/material';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import { useEffect, useMemo, useState } from 'react';
import {
 Control,
 Controller,
 UseFormSetValue,
 useWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { WidgetProgressProps } from './Dashboard.types';

dayjs.extend(weekOfYear);

interface DashboardWidgetModalDateProps {
 control: Control<WidgetProgressProps>;
 setValue: UseFormSetValue<WidgetProgressProps>;
 errors?: {
  dateFrom?: { message?: string };
  dateTo?: { message?: string };
 };
}

const DashboardWidgetModalDate = ({
 control,
 setValue,
 errors,
}: DashboardWidgetModalDateProps) => {
 const { t } = useTranslation();
 const [period, setPeriod] = useState<string>('');

 const dateFrom = useWatch({ control, name: 'dateFrom' });
 const dateTo = useWatch({ control, name: 'dateTo' });

 const now = useMemo(() => dayjs(), []);

 const periods = useMemo(
  () => [
   {
    value: [now.startOf('isoWeek'), now.endOf('isoWeek')],
    label: t('filterSidebar.thisWeek'),
   },
   {
    value: [
     now.subtract(7, 'day').startOf('isoWeek'),
     now.subtract(7, 'day').endOf('isoWeek'),
    ],
    label: t('filterSidebar.lastWeek'),
   },
   {
    value: [now.startOf('month'), now.endOf('month')],
    label: t('filterSidebar.thisMonth'),
   },
   {
    value: [
     now.subtract(1, 'month').startOf('month'),
     now.subtract(1, 'month').endOf('month'),
    ],
    label: t('filterSidebar.lastMonth'),
   },
   {
    value: [now.startOf('year'), now.endOf('year')],
    label: t('filterSidebar.thisYear'),
   },
   {
    value: [
     now.subtract(1, 'year').startOf('year'),
     now.subtract(1, 'year').endOf('year'),
    ],
    label: t('filterSidebar.lastYear'),
   },
  ],
  [now, t],
 );

 const formatDateToString = (date: Dayjs | null): string =>
  date ? date.format('YYYY-MM-DD') : '';

 const parseStringToDayjs = (dateString: string | undefined): Dayjs | null =>
  dateString ? dayjs(dateString) : null;

 useEffect(() => {
  const checkPeriodMatch = (
   fromDate: string | undefined,
   toDate: string | undefined,
  ): string => {
   if (!fromDate || !toDate) {
    return '';
   }

   const from = parseStringToDayjs(fromDate);
   const to = parseStringToDayjs(toDate);

   if (!from || !to) {
    return '';
   }

   for (let i = 0; i < periods.length; i += 1) {
    const periodStart = periods[i].value[0];
    const periodEnd = periods[i].value[1];

    if (from.isSame(periodStart, 'day') && to.isSame(periodEnd, 'day')) {
     return String(i);
    }
   }

   return '';
  };

  const matchedPeriod = checkPeriodMatch(dateFrom, dateTo);
  setPeriod(matchedPeriod);
 }, [dateFrom, dateTo, periods]);

 return (
  <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="lt">
   <Box>
    <Typography
     sx={{
      mb: '12px',
      fontSize: '14px',
      color: (theme) => theme.palette.kemek.darkGray,
      fontWeight: 500,
     }}
    >
     Data
    </Typography>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
     <Box>
      <FormControl fullWidth>
       <InputLabel id="date-range-select-label">
        {t('filterSidebar.period')}
       </InputLabel>
       <Select
        id="date-range-select"
        labelId="date-range-select-label"
        label={t('filterSidebar.period')}
        value={period}
        onChange={(e) => {
         setPeriod(e.target.value);

         if (e.target.value === '') {
          setValue('dateFrom', '');
          setValue('dateTo', '');
         } else {
          const periodIndex = Number(e.target.value);
          const selectedPeriod = periods[periodIndex];
          if (selectedPeriod) {
           setValue('dateFrom', formatDateToString(selectedPeriod.value[0]));
           setValue('dateTo', formatDateToString(selectedPeriod.value[1]));
          }
         }
        }}
        sx={{
         height: '100%',
         background: (theme) => theme.palette.background.paper,
         width: '100%',
        }}
        inputProps={{ 'aria-label': t('filterSidebar.period') }}
       >
        <MenuItem value="" selected>
         Tuščias
        </MenuItem>
        {periods.map((item, index) => (
         <MenuItem key={item.label} value={String(index)}>
          {item.label}
         </MenuItem>
        ))}
       </Select>
      </FormControl>
     </Box>
     <Box
      sx={{
       display: 'flex',
       flexDirection: { xs: 'column', md: 'row' },
       gap: '12px',
      }}
     >
      <Box sx={{ flex: 1 }}>
       <Controller
        name="dateFrom"
        control={control}
        // rules={{ required: 'Start date is required' }}
        render={({ field }) => (
         <DateTimePicker
          slotProps={{
           field: {
            clearable: true,
            onClear: () => {
             field.onChange('');
             setValue('dateFrom', '');
            },
           },
           textField: {
            error: !!errors?.dateFrom,
            helperText: errors?.dateFrom?.message,
            sx: { backgroundColor: (theme) => theme.palette.background.paper },
           },
          }}
          sx={{
           width: '100%',
          }}
          value={parseStringToDayjs(field.value)}
          onChange={(value) => {
           const dateString = formatDateToString(value);
           field.onChange(dateString);
           setValue('dateFrom', dateString);
          }}
          label={t('filterSidebar.dateFrom')}
         />
        )}
       />
      </Box>
      <Box sx={{ flex: 1 }}>
       <Controller
        name="dateTo"
        control={control}
        // rules={{ required: 'End date is required' }}
        render={({ field }) => (
         <DateTimePicker
          slotProps={{
           field: {
            clearable: true,
            onClear: () => {
             field.onChange('');
             setValue('dateTo', '');
            },
           },
           textField: {
            error: !!errors?.dateTo,
            helperText: errors?.dateTo?.message,
            sx: { backgroundColor: (theme) => theme.palette.background.paper },
           },
          }}
          sx={{
           width: '100%',
          }}
          value={parseStringToDayjs(field.value)}
          onChange={(value) => {
           const dateString = formatDateToString(value);
           field.onChange(dateString);
           setValue('dateTo', dateString);
          }}
          label={t('filterSidebar.dateTo')}
         />
        )}
       />
      </Box>
     </Box>
    </Box>
   </Box>
  </LocalizationProvider>
 );
};

export default DashboardWidgetModalDate;
